import React, { useContext, useEffect, useMemo } from 'react';

import { Badge, Card } from '@ergeon/core-components';

import useCallControls from '../../hooks/useCallControls';
import { TwilioContext } from '../../context-providers/TwilioContext';
import { AgentContext } from '../../context-providers/AgentContext';
import useFetch from '../../hooks/useFetch';
import { fetchCustomerData, warmTransferToAgent } from '../../api';
import { GetCustomerAPIDataType } from '../../types';
import { ModalController } from '../../modals';
import AgentsListModal from '../../modals/AgentsListModal';

import CallDirectionIcon from './CallDirectionIcon';
import CustomerInfo from './CustomerInfo';
import ActiveCallControls from './ActiveCallControls';
import IncomingCallControls from './IncomingCallControls';
import { Customer, FloatingCallItemProps } from './types';

// Module-level cache that survives component unmount/remount
const customerCache = new Map<string, Customer>();

const FloatingCallItem: React.FC<FloatingCallItemProps> = ({ call }) => {
  const {
    getCustomerPhoneNumber,
    toggleMute,
    status,
    accept,
    decline,
    hangUp,
    isMuted,
    isHolded,
    loadingStates,
    callDuration,
    holdCall,
    hangupCooldown,
    holdPending,
  } = useCallControls(call);

  const { currentCall } = useContext(TwilioContext);
  const { identity } = useContext(AgentContext);
  const agentsModal = ModalController.useModal(AgentsListModal);
  const phoneNumber = getCustomerPhoneNumber() || '';
  const { data } = useFetch<GetCustomerAPIDataType>(
    () => fetchCustomerData(phoneNumber) as Promise<GetCustomerAPIDataType>,
    [phoneNumber],
  );

  // Store customer data in module-level cache (survives component remounts)
  useEffect(() => {
    if (data?.customer?.id && phoneNumber) {
      customerCache.set(phoneNumber, data.customer);
    }
  }, [data?.customer?.id, phoneNumber]);

  // Memoized customer lookup
  const customer = useMemo(
    () => (data?.customer?.id ? data.customer : customerCache.get(phoneNumber)),
    [data?.customer?.id, phoneNumber],
  );

  const isActive = useMemo(
    () => ['connecting', 'open', 'reconnecting', 'ringing', 'pending'].includes(status || ''),
    [status],
  );

  // Memoized badge props to avoid recalculation
  const badgeProps = useMemo(() => {
    const isWarmTransfer = currentCall?.customParameters.get('warm_transfer') === 'True';
    if (isWarmTransfer) {
      return { flavor: 'warning' as const, value: 'Warm transfer' };
    }
    const statusText = status || '';
    const capitalizedStatus = statusText ? statusText.charAt(0).toUpperCase() + statusText.slice(1) : '';
    return { flavor: 'primary' as const, value: capitalizedStatus };
  }, [currentCall, status]);

  const handleWarmTransfer = () => {
    if (!identity) return;

    agentsModal.show({ excludeIdentity: identity }).then(async(agent) => {
      if (agent && call) {
        try {
          await warmTransferToAgent({
            agentIdentity: agent.identity,
            callSid: call.parameters.CallSid,
          });
        } catch (error) {
          console.error('Warm transfer failed:', error);
        }
      }
    });
  };

  const inviteAgentDisabled = !identity || status !== 'open';

  return (
    <Card className="py-4 pr-6 pl-4" flavor="regular" zIndex="4">
      <div className="flex items-start gap-2 w-full">
        <CallDirectionIcon direction={call.direction} isActive={isActive} />

        <div className="flex justify-between items-center gap-2 w-full">
          <div className="flex flex-col gap-1">
            <CustomerInfo customer={customer} phoneNumber={phoneNumber} />
            <Badge flavor={badgeProps.flavor} value={badgeProps.value} />
          </div>

          {isActive && status !== 'pending' && (
            <ActiveCallControls
              callDuration={callDuration}
              hangUp={hangUp}
              hangupCooldown={hangupCooldown}
              holdCall={holdCall}
              holdPending={holdPending}
              inviteAgentDisabled={inviteAgentDisabled}
              isHolded={isHolded}
              isMuted={isMuted}
              loadingStates={loadingStates}
              onInviteAgent={handleWarmTransfer}
              toggleMute={toggleMute} />
          )}

          {status === 'pending' && (
            <IncomingCallControls loadingStates={loadingStates} onAccept={accept} onDecline={decline} />
          )}
        </div>
      </div>
    </Card>
  );
};

export default FloatingCallItem;
